import { formatOrgNum } from "@/utils/other";
import {
  BeneficialOwnersPortfolioStatus,
  CompanyState,
  concatenateShortcuts,
  formatARPortfolioDetails,
  formatBeneficialOwnersPortfolioDetails,
  LastTransaction,
  PortfolioCompany,
  ShareholderRegisterStatementPortfolioStatus,
} from "@/stores/PortfolioStore";
import { compact } from "lodash";
import { AccessType } from "@/types/enums/AccessType";
import { Button, CopyToClipboardButton } from "@/components-v2/Button";
import { StarButton } from "@/components-v2/StarButton";
import { Tag } from "@/components-v2/Tag";
import { Icon } from "@/components-v2/Icon";
import { Box, Card, Grid, GridItem, VBox } from "@/components-v2/Box";
import { observer } from "mobx-react";
import { FC, useEffect } from "react";
import { DataTable } from "@/components-v2/DataTable";
import { DropdownMenu, DropdownMenuItemProps } from "@/components-v2/Dropdown";
import {
  navigateToAdminDashboard,
  navigateToProjectOverview,
  navigateToShareholdersList,
  navigateToTransactions,
} from "@/utils/navigation";
import { stopPropagation } from "@/utils/events";
import { useWindowWidth } from "@/utils/screen";
import { MeetingType } from "@/meetings/meetingsDomain";
import { TFunction } from "@/i18n/tt";
import { useLanguage, useRootStore, useTranslation } from "@/stores/RootStoreContext";
import { PortfolioActions, PortfolioCompaniesTab } from "@/portfolio/portfolioDomain";
import { formatPercent } from "@/utils/numbers";
import { GetUserSettingsResponse } from "@/types/UserInfo";
import { when } from "@/utils/functions";
import { toDate } from "@/utils/Dates";
import { transactionLabel } from "@/types/enums/TransactionType";
import { HoverTooltip } from "@/components-v2/Tooltip";
import { isOk, isPending, Result } from "@/common/common";
import { AccessTypeTag } from "@/common/commonComponents";
import { Transaction } from "@/types/Transactions";
import { LoadingIndicator } from "@/components-v2/LoadingIndicator";
import { isNotRegisteredIssue } from "@/shareholderRegister/transactions/transactionLogDomain";

export const CompanyTable: FC<{
  data: PortfolioCompany[];
  activeTab: PortfolioCompaniesTab;
  onSelect: (company: PortfolioCompany) => void;
  onClickStar: (organisationNumber: string, accessType: AccessType) => void;
  isRenderStar: (organisationNumber: string, accessType: AccessType) => boolean;
  portfolioActions: PortfolioActions;
  userSettings: GetUserSettingsResponse | null;
  isNoActiveSubscription: (orgNo: string) => boolean;
  hoveredTransactionData?: Result<Transaction>;
}> = observer(
  ({
    data,
    activeTab,
    onSelect,
    onClickStar,
    isRenderStar,
    portfolioActions,
    userSettings,
    isNoActiveSubscription,
    hoveredTransactionData,
  }) => {
    const windowWidth = useWindowWidth();
    if (windowWidth === "sm") {
      return (
        <SmallScreenCompanyTable
          data={data}
          activeTab={activeTab}
          onSelect={onSelect}
          onClickStar={onClickStar}
          isRenderStar={isRenderStar}
        />
      );
    }
    return (
      <DesktopScreenCompanyTable
        data={data}
        activeTab={activeTab}
        onSelect={onSelect}
        onClickStar={onClickStar}
        isRenderStar={isRenderStar}
        isLargeScreen={windowWidth !== "md"}
        portfolioActions={portfolioActions}
        userSettings={userSettings}
        isNoActiveSubscription={isNoActiveSubscription}
        hoveredTransactionData={hoveredTransactionData}
      />
    );
  },
);

function renderUnconnectedCompanyColumn(c: PortfolioCompany, t: TFunction) {
  return c.state === CompanyState.DRAFT ? (
    <Box alignItems={"center"}>
      <Button
        variant={"secondary"}
        label={t("portfolio.askForPermissionButton")}
        size={"xs"}
        fullWidth
      />
      <Icon name={"info"} data-tooltip={t("portfolio.askForPermissionTooltipText")} />
    </Box>
  ) : c.accessType === AccessType.NONE ? (
    <Box alignItems={"center"}>
      <Button
        icon={"plugs"}
        label={t("portfolio.connectCompanyButton")}
        size={"xs"}
        variant={"secondary"}
        fullWidth
      />
    </Box>
  ) : c.state === CompanyState.PENDING ? (
    <Box alignItems={"center"} className={"subtle text-sm sm:justify-end"}>
      <span>{t("portfolio.pendingPermissionRequest")}</span>
      <Icon name={"info"} data-tooltip={t("portfolio.pendingPermissionTooltipText")} />
    </Box>
  ) : undefined;
}

function CompanyAccessTag({ company }: { company: PortfolioCompany }) {
  const { t } = useTranslation();
  return company.state === CompanyState.DRAFT && company.accessType === AccessType.NONE ? (
    <Tag color="error" label={t("portfolio.askForPermissionDraftPill")} />
  ) : company.accessType === AccessType.NONE ? null : (
    <AccessTypeTag accessType={company.accessType} customRole={company.role} />
  );
}

export const DesktopScreenCompanyTable: FC<{
  data: PortfolioCompany[];
  activeTab: PortfolioCompaniesTab;
  onSelect: (company: PortfolioCompany) => void;
  onClickStar: (organisationNumber: string, accessType: AccessType) => void;
  isRenderStar: (organisationNumber: string, accessType: AccessType) => boolean;
  isLargeScreen: boolean;
  portfolioActions: PortfolioActions;
  userSettings: GetUserSettingsResponse | null;
  isNoActiveSubscription: (orgNo: string) => boolean;
  hoveredTransactionData?: Result<Transaction>;
}> = observer(
  ({
    data,
    activeTab,
    onSelect,
    onClickStar,
    isRenderStar,
    isLargeScreen,
    portfolioActions,
    userSettings,
    isNoActiveSubscription,
    hoveredTransactionData,
  }) => {
    const { t } = useTranslation();
    const locale = useLanguage();
    const isOnboardedCompanyTab =
      activeTab !== "not-connected" &&
      activeTab !== "beneficial-owners" &&
      activeTab !== "ar-status";
    return (
      <DataTable<PortfolioCompany>
        inline
        getId={(c) => `${c.organisationNumber}_${c.accessType}_${c.shareholderId}`}
        variant={"LARGE"}
        onRowClick={onSelect}
        tableData={data}
        initialSortBy={"lastVisited"}
        initialSortDirection={"desc"}
        tableColumns={compact([
          {
            align: "left",
            name: "star",
            label: "",
            width: "1%",
            notSorted: true,
            getValue: (c) =>
              c.isCustomer && (
                <StarButton
                  isStarred={isRenderStar(c.organisationNumber, c.accessType)}
                  onClick={() => onClickStar(c.organisationNumber, c.accessType)}
                  tooltip={t("portfolio.star-tooltip")}
                />
              ),
          },
          {
            align: "left",
            name: "name",
            label: t("portfolio.table.header.company"),
            getSortValue: (c) => c.name.toLocaleLowerCase(),
            getValue: (c) => (
              <VBox>
                <div>{c.name}</div>
                {c.accessType === AccessType.SHAREHOLDER ? (
                  <div className={"subtle text-sm"}>{t("portfolio.my-investments")}</div>
                ) : c.accessType === AccessType.ADDITIONAL_REPRESENTATIVE ? (
                  <div className={"subtle text-sm"}>{t("portfolio.my-investments")}</div>
                ) : c.accessType === AccessType.BOARD_MEMBER ? (
                  <div className={"subtle text-sm"}>{t("portfolio.with-board-role")}</div>
                ) : c.portfolioName ? (
                  <div className={"subtle text-sm"}>{c.portfolioName}</div>
                ) : null}
                {(c.onBehalfOfShareholderCompany || c.onBehalfOfShareholderPerson) && (
                  <div className={"subtle text-sm"}>
                    {t("portfolio.onBehalfOf", {
                      name: c.onBehalfOfShareholderCompany ?? c.onBehalfOfShareholderPerson,
                    })}
                  </div>
                )}
                {isNoActiveSubscription(c.organisationNumber) && (
                  <div className={"subtle text-sm"}>{t("portfolio.noActiveSubscription")}</div>
                )}
              </VBox>
            ),
          },
          {
            align: "left",
            name: "organisationNumber",
            label: t("portfolio.table.header.organisation-number"),
            getValue: (c) => (
              <CopyToClipboardButton
                text={c.organisationNumber}
                label={formatOrgNum(c.organisationNumber)}
                withoutIcon
              />
            ),
          },
          isOnboardedCompanyTab && {
            align: "left",
            name: "accessType",
            label: t("portfolio.table.header.access-type"),
            getValue: (c) => <CompanyAccessTag company={c} />,
          },
          isLargeScreen &&
            isOnboardedCompanyTab &&
            data.some((c) => c.accessType === AccessType.ADMIN) && {
              align: "left",
              name: "lastTransaction",
              label: t("portfolio.table.header.last.transaction"),
              getValue: (c) =>
                c.accessType === AccessType.ADMIN && (
                  <LastTransactionView
                    lastTransaction={c.lastTransaction}
                    navigateToLastTransaction={portfolioActions.navigateToLastTransaction}
                    orgNo={c.organisationNumber}
                    fetchTransactionData={portfolioActions.onHoverLastTransaction}
                    hoveredTransactionData={hoveredTransactionData}
                  />
                ),
              getSortValue: (c) =>
                c.lastTransaction?.date ? toDate(c.lastTransaction.date).getTime() : 0,
            },
          isLargeScreen &&
            isOnboardedCompanyTab && {
              align: "right",
              name: "numberOfShareholders",
              label: t("portfolio.table.header.shareholders"),
              getValue: (c) => c.numberOfShareholders,
            },
          isLargeScreen &&
            activeTab === "not-connected" && {
              align: "left",
              name: "ownershipInPercent",
              label: t("ownership"),
              getValue: (c) => (
                <Box>
                  {c.sourceOrgNoName}{" "}
                  <div className={"subtle"}>{formatPercent(locale, c.ownershipInPercent)}</div>
                </Box>
              ),
            },
          activeTab === "ar-status" && {
            align: "left",
            name: "arStatus",
            label: t("status"),
            getSortValue: (c) => c.shareholderRegisterStatementStatus?.type,
            getValue: (c) => <ARStatusTag status={c.shareholderRegisterStatementStatus} />,
          },
          activeTab === "ar-status" && {
            align: "left",
            name: "arStatusDetails",
            label: t("details"),
            getSortValue: (c) =>
              t(formatARPortfolioDetails(locale, c.shareholderRegisterStatementStatus)),
            getValue: (c) => formatARPortfolioDetails(locale, c.shareholderRegisterStatementStatus),
          },
          activeTab === "beneficial-owners" && {
            align: "left",
            name: "beneficialOwnerPortfolioStatus",
            label: t("status"),
            getSortValue: (c) => c.beneficialOwnersPortfolioStatus?.type,
            getValue: (c) => (
              <BeneficialOwnersStatusTag status={c.beneficialOwnersPortfolioStatus} />
            ),
          },
          activeTab === "beneficial-owners" && {
            align: "left",
            name: "beneficialOwnerPortfolioDetails",
            label: t("details"),
            getSortValue: (c) =>
              t(formatBeneficialOwnersPortfolioDetails(locale, c.beneficialOwnersPortfolioStatus)),
            getValue: (c) =>
              formatBeneficialOwnersPortfolioDetails(locale, c.beneficialOwnersPortfolioStatus),
          },
          activeTab !== "not-connected" && {
            align: "left",
            name: "lastVisited",
            label: { key: "last-visited" },
            getSortValue: (c) =>
              when(userSettings?.lastVisitedCompanies?.[c.organisationNumber], (ts) =>
                toDate(ts).getTime(),
              ) ?? 0,
            getValue: (c) =>
              when(userSettings?.lastVisitedCompanies?.[c.organisationNumber], (ts) => ({
                type: "datetime",
                relative: true,
                value: ts,
              })),
          },
          {
            align: "right",
            name: "actions",
            label: "",
            notSorted: true,
            getValue: (c) =>
              c.accessType === AccessType.ADMIN ? (
                <CompanyNavigationMenu company={c} portfolioActions={portfolioActions} />
              ) : (
                <></>
              ),
          },
          !isOnboardedCompanyTab && {
            align: "right",
            name: "onboard",
            label: "",
            notSorted: true,
            getValue: (c) => renderUnconnectedCompanyColumn(c, t),
          },
        ])}
      />
    );
  },
);

function ARStatusTag({
  status,
}: {
  status: ShareholderRegisterStatementPortfolioStatus | undefined;
}) {
  switch (status?.type) {
    case "Approved":
      return <Tag label={{ key: "approved" }} color={"success"} />;
    case "Rejected":
      return <Tag label={{ key: "rejected" }} color={"error"} />;
    case "Sent":
      return <Tag label={{ key: "submitted" }} color={"info"} />;
    case "NotSent":
      return <Tag label={{ key: "not-submitted" }} color={"neutral"} />;
    default:
      return null;
  }
}

function BeneficialOwnersStatusTag({
  status,
}: {
  status: BeneficialOwnersPortfolioStatus | undefined;
}) {
  switch (status?.type) {
    case "Sent":
      return <Tag label={{ key: "submitted" }} color={"success"} />;
    case "NotSent":
      return <Tag label={{ key: "not-submitted" }} color={"neutral"} />;
    case "Overdue":
      return null;
    default:
      return null;
  }
}

export const CompanyNavigationMenu: FC<{
  company: PortfolioCompany;
  portfolioActions: PortfolioActions;
}> = observer(({ company, portfolioActions }) => {
  const { t } = useTranslation();
  const history = useRootStore().history;
  const locale = useLanguage();

  const standardShortcuts: DropdownMenuItemProps[] = [
    {
      type: "item",
      label: t("portfolio.links.dashboard"),
      onSelect: () => navigateToAdminDashboard(company.organisationNumber, history),
    },
    {
      type: "item",
      label: t("portfolio.links.add-admin"),
      onSelect: () =>
        portfolioActions.onAddAdministrator({
          companyItem: {
            organisationNumber: company.organisationNumber,
            companyName: company.name,
          },
          lang: locale,
        }),
    },
    {
      type: "item",
      label: t("portfolio.links.edit-shareholder"),
      onSelect: () => navigateToShareholdersList(company.organisationNumber, history),
    },
    {
      type: "item",
      label: t("portfolio.links.new-transaction"),
      onSelect: () => navigateToTransactions(company.organisationNumber, history),
    },
  ];
  const advancedShortcuts: DropdownMenuItemProps[] = [
    {
      type: "item",
      label: t("portfolio.links.new-company-event"),
      onSelect: () => navigateToProjectOverview(company.organisationNumber, history),
    },
    {
      type: "item",
      label: t("portfolio.links.new-board-meeting"),
      onSelect: () =>
        portfolioActions.onNewMeeting(company.organisationNumber, MeetingType.BOARD_MEETING),
    },
    {
      type: "item",
      label: t("portfolio.links.new-general-meeting"),
      onSelect: () =>
        portfolioActions.onNewMeeting(company.organisationNumber, MeetingType.GENERAL_MEETING),
    },
  ];
  const portfolioInstanceShortcuts: DropdownMenuItemProps[] = compact([
    portfolioActions.onRemoveFromPortfolio && {
      type: "item",
      label: t("portfolio.links.remove-from-portfolio"),
      onSelect: () => portfolioActions.onRemoveFromPortfolio?.(company),
    },
    portfolioActions.onMoveToPortfolio && {
      type: "item",
      label: t("portfolio.links.move-to-portfolio"),
      onSelect: () => portfolioActions.onMoveToPortfolio?.(company),
    },
  ]);
  return (
    <DropdownMenu
      button={{
        type: "dotdotdot",
        tooltip: t("options"),
      }}
      items={concatenateShortcuts<DropdownMenuItemProps>(
        standardShortcuts,
        advancedShortcuts,
        portfolioInstanceShortcuts,
      )}
      alignment={"end"}
    />
  );
});

export const SmallScreenCompanyTable: FC<{
  data: PortfolioCompany[];
  activeTab: PortfolioCompaniesTab;
  onSelect: (company: PortfolioCompany) => void;
  onClickStar: (organisationNumber: string, accessType: AccessType) => void;
  isRenderStar: (organisationNumber: string, accessType: AccessType) => boolean;
}> = observer(({ data, activeTab, onSelect, onClickStar, isRenderStar }) => {
  const { t } = useTranslation();
  return (
    <Card padding={false}>
      <VBox gap={0}>
        {data.map((c) => {
          const hasAccess =
            c.accessType !== AccessType.NONE &&
            c.accessType !== AccessType.DRAFT &&
            c.state !== CompanyState.PENDING &&
            c.state !== CompanyState.DRAFT;
          return (
            <div
              className={"cursor-pointer"}
              key={c.id + c.organisationNumber + c.accessType + c.shareholderId}
              onClick={() => onSelect(c)}
            >
              <Grid className={"hover:bg-alt2"} grow alignItems={"center"} borderBottom padding>
                <GridItem xs={11} sm={4}>
                  <Box alignItems={"center"}>
                    {c.isCustomer && (
                      <StarButton
                        isStarred={isRenderStar(c.organisationNumber, c.accessType)}
                        onClick={() => onClickStar(c.organisationNumber, c.accessType)}
                        tooltip={t("portfolio.star-tooltip")}
                      />
                    )}

                    <div>
                      <div>{c.name}</div>
                      {(c.onBehalfOfShareholderCompany || c.onBehalfOfShareholderPerson) && (
                        <div className={"subtle text-sm"}>
                          {t("portfolio.onBehalfOf", {
                            name: c.onBehalfOfShareholderCompany ?? c.onBehalfOfShareholderPerson,
                          })}
                        </div>
                      )}
                    </div>
                  </Box>
                </GridItem>
                <GridItem
                  xs={hasAccess ? 11 : 12}
                  sm={2}
                  whitespace={"nowrap"}
                  overflowX={"hidden"}
                  className={"text-ellipsis subtle"}
                  textSize={"sm"}
                >
                  {formatOrgNum(c.organisationNumber)}
                </GridItem>
                <GridItem xs={hasAccess ? 11 : 12} sm={2} overflow={"hidden"}>
                  {activeTab === "beneficial-owners" ? (
                    <BeneficialOwnersStatusTag status={c.beneficialOwnersPortfolioStatus} />
                  ) : activeTab === "ar-status" ? (
                    <ARStatusTag status={c.shareholderRegisterStatementStatus} />
                  ) : (
                    <CompanyAccessTag company={c} />
                  )}
                </GridItem>
                <GridItem xs={hasAccess ? 11 : 12} sm={hasAccess ? 3 : 4}>
                  {renderUnconnectedCompanyColumn(c, t)}
                </GridItem>
                {hasAccess && (
                  <GridItem
                    textAlign={"right"}
                    className={"max-sm:row-start-1 max-sm:row-span-4 max-sm:col-start-12"}
                  >
                    {<Icon name={"caret-right"} />}
                  </GridItem>
                )}
              </Grid>
            </div>
          );
        })}
      </VBox>
    </Card>
  );
});

const LastTransactionTooltip = observer(
  ({
    lastTransaction,
    transactionData,
    fetchTransactionData,
  }: {
    lastTransaction: LastTransaction;
    transactionData: Result<Transaction> | undefined;
    fetchTransactionData: (transactionLogId: number) => void;
  }) => {
    const language = useLanguage();

    useEffect(() => {
      fetchTransactionData(lastTransaction.transactionLogId);
      // Note: cannot have `fetchTransactionData` in the useEffect dependency array as it is a new function on every render
      // eslint-disable-next-line
    }, [lastTransaction.transactionLogId]);

    const { t, f } = useTranslation();
    return (
      <VBox>
        <div>
          {t(transactionLabel[lastTransaction.transactionType])}{" "}
          {f.formatDate(lastTransaction.date)}
          {":"}
        </div>
        {isPending(transactionData) ? (
          <LoadingIndicator label={""} size={"md"} />
        ) : isOk(transactionData) ? (
          <VBox>
            <div>
              {transactionData.data.descriptions.find((d) => d.language === language)?.text}
            </div>
            {
              isNotRegisteredIssue(transactionData.data) ? (
                <VBox>
                  <Tag color={"warning"} label={t("transactions.not-registered-in-br")} />
                  <div>{t("transactions.not-registered-in-br.tooltip")}</div>
                </VBox>
              ) : null
              // isAfterNotRegisteredIssue && (
              //   <VBox alignItems={"center"} textAlign={"center"}>
              //     <Tag color={"neutral"} label={t("transactions.not-reported")} />
              //     <div>{t("transactions.not-reported.info")}</div>
              //   </VBox>
              // )
            }
          </VBox>
        ) : undefined}
      </VBox>
    );
  },
);

const LastTransactionView = observer(
  ({
    lastTransaction,
    orgNo,
    navigateToLastTransaction,
    fetchTransactionData,
    hoveredTransactionData,
  }: {
    lastTransaction: LastTransaction | undefined;
    orgNo: string;
    navigateToLastTransaction: (orgNo: string, transactionId: number) => void;
    fetchTransactionData: (transactionLogId: number) => void;
    hoveredTransactionData: Result<Transaction> | undefined;
  }) => {
    const { t, f } = useTranslation();

    if (lastTransaction) {
      return (
        <HoverTooltip
          content={
            <LastTransactionTooltip
              lastTransaction={lastTransaction}
              transactionData={hoveredTransactionData}
              fetchTransactionData={fetchTransactionData}
            />
          }
        >
          {({ targetRef }) => (
            <button
              ref={targetRef}
              className={"flex gap-2 items-center hover:underline"}
              onClick={stopPropagation(() =>
                navigateToLastTransaction(orgNo, lastTransaction.transactionLogId),
              )}
            >
              <VBox>
                <div>{t(transactionLabel[lastTransaction.transactionType])}</div>
                <div className={"subtle text-sm"}>{f.formatDate(lastTransaction.date)}</div>
              </VBox>
            </button>
          )}
        </HoverTooltip>
      );
    }
    return <div>{t("no-transactions")}</div>;
  },
);
